import React, { useState, useCallback, useEffect } from 'react';
import TopBar from '../components/layout/TopBar';
import { Icon } from '@iconify/react';
import {
  GlassCard, Button, Badge, InputField, Modal,
  SectionHeader, DataTable, Divider, StatCard,
} from '../components/ui';
import { useToast } from '../context/ToastContext';
import { api } from '../lib/api';

// ─── Types ──────────────────────────────────────────────────

interface Beneficiary {
  id: string;
  firstName: string;
  lastName: string;
  relationship: string;
  phone?: string;
  email?: string;
  birthday?: string;
}

interface ContactLog {
  id: string;
  type: string;
  subject: string;
  body?: string;
  createdAt: string;
  agent: { id: string; name: string };
}

interface SaleRef {
  id: string;
  saleAmount: number;
  totalPaid: number;
  balance: number;
  stage: string;
  lot: { lotNumber: string; sectionId: string };
  createdAt: string;
}

interface LedgerEntry {
  id: string;
  type: string;
  amount: number;
  description: string;
  createdAt: string;
}

interface Client {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone: string;
  address?: string;
  birthday?: string;
  occupation?: string;
  incomeBracket?: string;
  source: string;
  status: string;
  emergencyName?: string;
  emergencyPhone?: string;
  notes?: string;
  assignedAgent?: { id: string; name: string; email: string };
  beneficiaries: Beneficiary[];
  contactLogs: ContactLog[];
  sales: SaleRef[];
  ledgerEntries?: LedgerEntry[];
  _count?: { beneficiaries: number; sales: number; contactLogs: number };
  createdAt: string;
}

type View = 'list' | 'detail' | 'form';

const CLIENT_STATUSES = ['LEAD', 'PROSPECT', 'ACTIVE', 'INACTIVE'] as const;
const CLIENT_SOURCES = ['WALK_IN', 'REFERRAL', 'ONLINE', 'SOCIAL_MEDIA', 'EVENT', 'OTHER'] as const;
const INCOME_BRACKETS = ['Below 20K', '20K-50K', '50K-100K', '100K+'] as const;
const RELATIONSHIPS = ['Spouse', 'Child', 'Parent', 'Sibling', 'Other'] as const;
const CONTACT_TYPES = ['CALL', 'EMAIL', 'VISIT', 'SMS', 'NOTE'] as const;

// ─── Helpers ────────────────────────────────────────────────

const statusVariant = (s: string) => {
  switch (s) {
    case 'ACTIVE': return 'available';
    case 'PROSPECT': return 'info';
    case 'LEAD': return 'reserved';
    case 'INACTIVE': return 'sold';
    default: return 'default';
  }
};

const stageVariant = (s: string) => {
  switch (s) {
    case 'COMPLETED': return 'available';
    case 'PAYMENT': case 'CONTRACT_SIGNING': return 'info';
    case 'INQUIRY': case 'RESERVATION': return 'reserved';
    case 'CANCELLED': return 'sold';
    default: return 'default';
  }
};

const contactIcon = (type: string) => {
  switch (type) {
    case 'CALL': return 'solar:phone-bold-duotone';
    case 'EMAIL': return 'solar:letter-bold-duotone';
    case 'VISIT': return 'solar:map-point-bold-duotone';
    case 'SMS': return 'solar:chat-round-bold-duotone';
    case 'NOTE': return 'solar:document-text-bold-duotone';
    default: return 'solar:info-circle-bold-duotone';
  }
};

const ledgerTypeConfig: Record<string, { label: string; icon: string; color: string }> = {
  PAYMENT: { label: 'Payment', icon: 'solar:wallet-money-bold-duotone', color: '#10B981' },
  PENALTY: { label: 'Penalty', icon: 'solar:danger-triangle-bold-duotone', color: '#EF4444' },
  ADJUSTMENT: { label: 'Adjustment', icon: 'solar:pen-new-square-bold-duotone', color: '#F59E0B' },
  REFUND: { label: 'Refund', icon: 'solar:undo-left-bold-duotone', color: '#8B5CF6' },
  DOWN_PAYMENT: { label: 'Down Payment', icon: 'solar:hand-money-bold-duotone', color: '#0D9488' },
};

const formatDate = (d?: string) => d ? new Date(d).toLocaleDateString('en-PH', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
const fmt = (n: number) => '₱' + n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

// ─── Component ──────────────────────────────────────────────

const Clients: React.FC = () => {
  const { addToast } = useToast();
  const [view, setView] = useState<View>('list');
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Client | null>(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Form state
  const [formData, setFormData] = useState<any>({});
  const [editingId, setEditingId] = useState<string | null>(null);
  const [beneficiaryForm, setBeneficiaryForm] = useState<any>({});
  const [showBeneficiaryModal, setShowBeneficiaryModal] = useState(false);

  // Contact log form
  const [showLogModal, setShowLogModal] = useState(false);
  const [logForm, setLogForm] = useState<any>({ type: 'CALL', subject: '', body: '' });

  // Portal account state
  const [portalStatus, setPortalStatus] = useState<any>(null);
  const [inviteLoading, setInviteLoading] = useState(false);

  // ─── Fetch clients from API ─────────────────────────────
  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.clients.list();
      const list = (data.clients || []).map((c: any) => ({
        ...c,
        beneficiaries: c.beneficiaries || [],
        contactLogs: c.contactLogs || [],
        sales: c.sales || [],
      }));
      setClients(list);
    } catch (err: any) {
      addToast('error', 'Fetch Error', err.message || 'Failed to load clients');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  // ─── Filtered list ──────────────────────────────────────
  const filtered = clients.filter((c) => {
    const matchSearch = !search || `${c.firstName} ${c.lastName} ${c.email || ''} ${c.phone}`.toLowerCase().includes(search.toLowerCase());
    const matchStatus = !filterStatus || c.status === filterStatus;
    return matchSearch && matchStatus;
  });

  // ─── Table columns ─────────────────────────────────────
  const columns = [
    {
      key: 'name', header: 'Client',
      render: (row: Client) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{
            width: '36px', height: '36px', borderRadius: 'var(--radius-md)',
            background: 'rgba(13, 148, 136, 0.15)', display: 'flex', alignItems: 'center',
            justifyContent: 'center', color: 'var(--primary-400)', fontWeight: 700, fontSize: '14px',
            flexShrink: 0,
          }}>
            {row.firstName[0]}{row.lastName[0]}
          </div>
          <div>
            <div style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{row.firstName} {row.lastName}</div>
            <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>{row.email || row.phone}</div>
          </div>
        </div>
      ),
    },
    { key: 'phone', header: 'Phone' },
    {
      key: 'source', header: 'Source',
      render: (row: Client) => <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>{row.source.replace('_', ' ')}</span>,
    },
    {
      key: 'status', header: 'Status',
      render: (row: Client) => <Badge variant={statusVariant(row.status) as any}>{row.status}</Badge>,
    },
    {
      key: 'agent', header: 'Agent',
      render: (row: Client) => <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>{row.assignedAgent?.name || '—'}</span>,
    },
    {
      key: 'sales', header: 'Sales',
      render: (row: Client) => <span style={{ color: 'var(--text-secondary)' }}>{row._count?.sales || 0}</span>,
    },
  ];

  // ─── Portal Account ────────────────────────────────────
  const fetchPortalStatus = useCallback(async (clientId: string) => {
    try {
      const data = await api.clientAuth.status(clientId);
      setPortalStatus(data);
    } catch { setPortalStatus(null); }
  }, []);

  // ─── Handlers ───────────────────────────────────────────

  const openDetail = useCallback(async (client: Client) => {
    try {
      const full = await api.clients.get(client.id);
      // Fetch ledger entries for this client
      let ledgerEntries: LedgerEntry[] = [];
      try {
        const ledgerData = await api.payments.ledger.list({ clientId: client.id });
        ledgerEntries = ledgerData.entries || [];
      } catch (e) {
        console.error('Failed to fetch ledger:', e);
      }
      setSelected({ 
        ...full, 
        beneficiaries: full.beneficiaries || [], 
        contactLogs: full.contactLogs || [], 
        sales: full.sales || [],
        ledgerEntries 
      });
      setView('detail');
      fetchPortalStatus(client.id);
    } catch {
      setSelected(client);
      setView('detail');
      fetchPortalStatus(client.id);
    }
  }, [fetchPortalStatus]);

  const openCreateForm = () => {
    setEditingId(null);
    setFormData({
      firstName: '', lastName: '', email: '', phone: '', address: '',
      birthday: '', occupation: '', incomeBracket: '', source: 'WALK_IN',
      status: 'LEAD', emergencyName: '', emergencyPhone: '', notes: '',
    });
    setView('form');
  };

  const openEditForm = (client: Client) => {
    setEditingId(client.id);
    setFormData({
      firstName: client.firstName, lastName: client.lastName,
      email: client.email || '', phone: client.phone, address: client.address || '',
      birthday: client.birthday?.split('T')[0] || '', occupation: client.occupation || '',
      incomeBracket: client.incomeBracket || '', source: client.source,
      status: client.status, emergencyName: client.emergencyName || '',
      emergencyPhone: client.emergencyPhone || '', notes: client.notes || '',
    });
    setView('form');
  };

  const handleSaveClient = async () => {
    if (!formData.firstName || !formData.lastName || !formData.phone) {
      addToast('error', 'Validation Error', 'First name, last name, and phone are required.');
      return;
    }

    try {
      if (editingId) {
        const updated = await api.clients.update(editingId, formData);
        setClients((prev) => prev.map((c) => c.id === editingId ? { ...c, ...updated } : c));
        setSelected((prev) => prev && prev.id === editingId ? { ...prev, ...updated } : prev);
        addToast('success', 'Client Updated', `${formData.firstName} ${formData.lastName} has been updated.`);
      } else {
        const created = await api.clients.create(formData);
        setClients((prev) => [{ ...created, beneficiaries: [], contactLogs: [], sales: [] }, ...prev]);
        addToast('success', 'Client Created', `${formData.firstName} ${formData.lastName} has been added.`);
      }
      setView(editingId && selected ? 'detail' : 'list');
    } catch (err: any) {
      addToast('error', 'Save Error', err.message || 'Failed to save client');
    }
  };

  const handleSendInvite = async (clientId: string) => {
    setInviteLoading(true);
    try {
      const data = await api.clientAuth.invite(clientId);
      addToast('success', 'Invitation Sent', `Email sent to ${data.email}`);
      fetchPortalStatus(clientId);
    } catch (err: any) {
      addToast('error', 'Failed', err.message || 'Could not send invitation');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleDeleteClient = async (id: string) => {
    try {
      await api.clients.delete(id);
      setClients((prev) => prev.filter((c) => c.id !== id));
      addToast('info', 'Client Deleted');
      setView('list');
      setSelected(null);
    } catch (err: any) {
      addToast('error', 'Delete Error', err.message || 'Failed to delete client');
    }
  };

  const handleAddBeneficiary = async () => {
    if (!beneficiaryForm.firstName || !beneficiaryForm.lastName || !beneficiaryForm.relationship) {
      addToast('error', 'Validation Error', 'First name, last name, and relationship are required.');
      return;
    }
    try {
      const newBeneficiary = await api.clients.addBeneficiary(selected!.id, beneficiaryForm);
      if (selected) {
        const updated = { ...selected, beneficiaries: [...selected.beneficiaries, newBeneficiary] };
        setSelected(updated);
        setClients((prev) => prev.map((c) => c.id === selected.id ? updated : c));
      }
      setBeneficiaryForm({});
      setShowBeneficiaryModal(false);
      addToast('success', 'Beneficiary Added');
    } catch (err: any) {
      addToast('error', 'Error', err.message || 'Failed to add beneficiary');
    }
  };

  const handleRemoveBeneficiary = async (bId: string) => {
    try {
      await api.clients.deleteBeneficiary(bId);
      if (selected) {
        const updated = { ...selected, beneficiaries: selected.beneficiaries.filter((b) => b.id !== bId) };
        setSelected(updated);
        setClients((prev) => prev.map((c) => c.id === selected.id ? updated : c));
        addToast('info', 'Beneficiary Removed');
      }
    } catch (err: any) {
      addToast('error', 'Error', err.message || 'Failed to remove beneficiary');
    }
  };

  const handleAddLog = async () => {
    if (!logForm.subject) {
      addToast('error', 'Validation Error', 'Subject is required.');
      return;
    }
    try {
      const user = JSON.parse(localStorage.getItem('vop_user') || '{}');
      const newLog = await api.clients.addContactLog(selected!.id, {
        agentId: user.id,
        type: logForm.type,
        subject: logForm.subject,
        body: logForm.body,
      });
      if (selected) {
        const updated = { ...selected, contactLogs: [newLog, ...selected.contactLogs] };
        setSelected(updated);
        setClients((prev) => prev.map((c) => c.id === selected.id ? updated : c));
      }
      setLogForm({ type: 'CALL', subject: '', body: '' });
      setShowLogModal(false);
      addToast('success', 'Contact Log Added');
    } catch (err: any) {
      addToast('error', 'Error', err.message || 'Failed to add contact log');
    }
  };

  // ─── RENDER: List View ──────────────────────────────────

  const renderListView = () => (
    <>
      <TopBar title="Clients" />
      <div style={{ padding: '28px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {/* Stats row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
          {[
            { label: 'Total', value: clients.length, color: 'var(--primary-400)', bg: 'rgba(13,148,136,0.12)' },
            { label: 'Leads', value: clients.filter(c => c.status === 'LEAD').length, color: 'var(--warning)', bg: 'rgba(245,158,11,0.12)' },
            { label: 'Prospects', value: clients.filter(c => c.status === 'PROSPECT').length, color: 'var(--info)', bg: 'rgba(59,130,246,0.12)' },
            { label: 'Active', value: clients.filter(c => c.status === 'ACTIVE').length, color: 'var(--success)', bg: 'rgba(34,197,94,0.12)' },
          ].map((s, i) => (
            <div key={i} style={{
              background: 'var(--glass-bg)', backdropFilter: 'var(--glass-blur)',
              border: '1px solid var(--glass-border)', borderRadius: 'var(--radius-lg)', padding: '16px 20px',
            }}>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{s.label}</div>
              <div style={{ fontSize: '24px', fontWeight: 700, color: s.color, fontFamily: 'var(--font-heading)', marginTop: '4px' }}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ flex: 1, maxWidth: '320px' }}>
            <InputField
              placeholder="Search clients..."
              icon="solar:magnifer-bold-duotone"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={{
              background: 'var(--input-bg)', border: '1px solid var(--input-border)',
              borderRadius: 'var(--radius-md)', padding: '10px 14px', color: 'var(--input-text)',
              fontFamily: 'var(--font-body)', fontSize: '14px', outline: 'none', cursor: 'pointer',
            }}
          >
            <option value="">All Statuses</option>
            {CLIENT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <div style={{ flex: 1 }} />
          <Button variant="primary" icon="solar:add-circle-bold-duotone" onClick={openCreateForm}>
            Add Client
          </Button>
        </div>

        {/* Table */}
        <DataTable columns={columns} data={filtered} onRowClick={openDetail} />
      </div>
    </>
  );

  // ─── RENDER: Detail View ────────────────────────────────

  const renderDetailView = () => {
    if (!selected) return null;

    // Calculate financial summary
    const totalSalesAmount = selected.sales.reduce((sum, s) => sum + s.saleAmount, 0);
    const totalPaid = selected.sales.reduce((sum, s) => sum + (s.totalPaid || 0), 0);
    const totalBalance = selected.sales.reduce((sum, s) => sum + (s.balance || 0), 0);

    return (
      <>
        <TopBar title={`${selected.firstName} ${selected.lastName}`} />
        <div style={{ padding: '20px 28px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Back + Actions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Button variant="ghost" icon="solar:arrow-left-bold" onClick={() => { setView('list'); setSelected(null); }}>
              Back to Clients
            </Button>
            <div style={{ flex: 1 }} />
            <Button variant="secondary" size="sm" icon="solar:chat-round-bold-duotone" 
              onClick={() => { setLogForm({ type: 'CALL', subject: '', body: '' }); setShowLogModal(true); }}>
              Quick Log
            </Button>
            <Button variant="secondary" icon="solar:pen-bold-duotone" onClick={() => openEditForm(selected)}>Edit</Button>
            <Button variant="danger" icon="solar:trash-bin-trash-bold-duotone" onClick={() => handleDeleteClient(selected.id)}>Delete</Button>
          </div>

          {/* PRIORITY 1: Financial Overview */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
            <StatCard label="Total Sales" value={fmt(totalSalesAmount)} 
              icon="solar:sale-bold-duotone" iconColor="#0D9488" iconBg="rgba(13,148,136,0.12)" />
            <StatCard label="Total Paid" value={fmt(totalPaid)} 
              icon="solar:wallet-money-bold-duotone" iconColor="#10B981" iconBg="rgba(16,185,129,0.12)" />
            <StatCard label="Balance Due" value={fmt(totalBalance)} 
              icon="solar:danger-triangle-bold-duotone" iconColor={totalBalance > 0 ? '#EF4444' : '#10B981'} 
              iconBg={totalBalance > 0 ? 'rgba(239,68,68,0.12)' : 'rgba(16,185,129,0.12)'} />
            <StatCard label="Active Sales" value={String(selected.sales.filter(s => !['COMPLETED', 'CANCELLED'].includes(s.stage)).length)} 
              icon="solar:document-bold-duotone" iconColor="#06B6D4" iconBg="rgba(6,182,212,0.12)" />
          </div>

          {/* PRIORITY 2: Client Profile + Status */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px' }}>
            <GlassCard>
              <SectionHeader title="Client Information" icon="solar:user-id-bold-duotone" />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginTop: '12px' }}>
                {[
                  ['Name', `${selected.firstName} ${selected.lastName}`],
                  ['Phone', selected.phone],
                  ['Email', selected.email || '—'],
                  ['Address', selected.address || '—'],
                  ['Birthday', formatDate(selected.birthday)],
                  ['Occupation', selected.occupation || '—'],
                  ['Income', selected.incomeBracket || '—'],
                  ['Source', selected.source.replace('_', ' ')],
                ].map(([label, value], i) => (
                  <div key={i}>
                    <div style={{ fontSize: '11px', color: 'var(--text-faint)', fontWeight: 600, marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
                    <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 500 }}>{value}</div>
                  </div>
                ))}
              </div>
            </GlassCard>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <GlassCard>
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    width: '64px', height: '64px', borderRadius: '16px', margin: '0 auto 12px',
                    background: 'rgba(13,148,136,0.15)', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', color: 'var(--primary-400)', fontWeight: 700, fontSize: '24px',
                    fontFamily: 'var(--font-heading)',
                  }}>{selected.firstName[0]}{selected.lastName[0]}</div>
                  <Badge variant={statusVariant(selected.status) as any}>{selected.status}</Badge>
                  <div style={{ marginTop: '8px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                    Agent: {selected.assignedAgent?.name || 'Unassigned'}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
                    Since {formatDate(selected.createdAt)}
                  </div>
                </div>
              </GlassCard>

              <GlassCard>
                <SectionHeader title="Emergency Contact" icon="solar:phone-bold-duotone" />
                {selected.emergencyName ? (
                  <div style={{ marginTop: '8px' }}>
                    <div style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{selected.emergencyName}</div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>{selected.emergencyPhone}</div>
                  </div>
                ) : (
                  <div style={{ color: 'var(--text-muted)', fontSize: '13px', marginTop: '8px' }}>Not provided</div>
                )}
              </GlassCard>

              {/* Portal Account */}
              <GlassCard>
                <SectionHeader title="Portal Account" icon="solar:login-3-bold-duotone" />
                <div style={{ marginTop: '8px' }}>
                  {!portalStatus || !portalStatus.hasAccount ? (
                    <>
                      <div style={{ color: 'var(--text-muted)', fontSize: '13px', marginBottom: '10px' }}>
                        {selected.email ? 'No portal account yet.' : 'Add an email to enable portal access.'}
                      </div>
                      {selected.email && (
                        <Button variant="primary" size="sm" icon="solar:letter-bold-duotone"
                          loading={inviteLoading} onClick={() => handleSendInvite(selected.id)}>
                          Send Invite
                        </Button>
                      )}
                    </>
                  ) : (
                    <>
                      <Badge variant={
                        portalStatus.status === 'active' ? 'available' as any :
                        portalStatus.status === 'invited' ? 'reserved' as any : 'sold' as any
                      }>
                        {portalStatus.status === 'active' ? 'Active' :
                         portalStatus.status === 'invited' ? 'Invited' : 'Deactivated'}
                      </Badge>
                      <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '6px' }}>
                        {portalStatus.email}
                      </div>
                      {portalStatus.lastLoginAt && (
                        <div style={{ fontSize: '11px', color: 'var(--text-faint)', marginTop: '4px' }}>
                          Last login: {formatDate(portalStatus.lastLoginAt)}
                        </div>
                      )}
                      {portalStatus.status === 'invited' && (
                        <Button variant="secondary" size="sm" icon="solar:refresh-bold-duotone"
                          loading={inviteLoading} onClick={() => handleSendInvite(selected.id)}
                          style={{ marginTop: '8px' }}>
                          Resend Invite
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </GlassCard>
            </div>
          </div>

          {/* PRIORITY 3: Sales History */}
          <GlassCard>
            <SectionHeader title={`Sales (${selected.sales.length})`} icon="solar:sale-bold-duotone" />
            {selected.sales.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '12px' }}>
                {selected.sales.map((s) => (
                  <div key={s.id} style={{
                    background: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)', padding: '14px 16px',
                    border: '1px solid var(--border-base)',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <div style={{ 
                          background: 'var(--primary-600)', color: '#fff', 
                          padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 700,
                        }}>
                          {s.lot.lotNumber}
                        </div>
                        <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '14px' }}>
                          Section {s.lot.sectionId}
                        </div>
                      </div>
                      <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>{formatDate(s.createdAt)}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ color: 'var(--text-primary)', fontWeight: 700, fontSize: '15px', marginBottom: '4px' }}>{fmt(s.saleAmount)}</div>
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', justifyContent: 'flex-end', fontSize: '11px' }}>
                        <span style={{ color: '#10B981' }}>Paid: {fmt(s.totalPaid || 0)}</span>
                        {s.balance > 0 && <span style={{ color: '#EF4444' }}>· Bal: {fmt(s.balance)}</span>}
                      </div>
                      <Badge variant={stageVariant(s.stage) as any} style={{ marginTop: '4px' }}>{s.stage.replace('_', ' ')}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ color: 'var(--text-muted)', fontSize: '13px', padding: '20px 0', textAlign: 'center' }}>No sales yet.</div>
            )}
          </GlassCard>

          {/* PRIORITY 4: Payment History */}
          {selected.ledgerEntries && selected.ledgerEntries.length > 0 && (
            <GlassCard>
              <SectionHeader title={`Payment History (${selected.ledgerEntries.length})`} icon="solar:wallet-money-bold-duotone" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '12px' }}>
                {selected.ledgerEntries.slice(0, 10).map((entry) => {
                  const cfg = ledgerTypeConfig[entry.type] || { label: entry.type, icon: 'solar:document-bold-duotone', color: 'var(--text-secondary)' };
                  return (
                    <div key={entry.id} style={{
                      display: 'flex', alignItems: 'center', gap: '12px',
                      padding: '10px 12px', borderRadius: 'var(--radius-sm)',
                      background: 'var(--bg-elevated)', border: '1px solid var(--border-base)',
                    }}>
                      <Icon icon={cfg.icon} width={18} style={{ color: cfg.color }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: 600 }}>{entry.description}</div>
                        <div style={{ color: 'var(--text-muted)', fontSize: '11px' }}>{formatDate(entry.createdAt)}</div>
                      </div>
                      <div style={{ color: entry.amount >= 0 ? '#10B981' : '#EF4444', fontWeight: 700, fontSize: '14px' }}>
                        {entry.amount >= 0 ? '+' : ''}{fmt(entry.amount)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </GlassCard>
          )}

          {/* PRIORITY 5: Contact History */}
          <GlassCard>
            <SectionHeader
              title={`Contact History (${selected.contactLogs.length})`}
              icon="solar:chat-round-bold-duotone"
              actions={
                <Button variant="secondary" size="sm" icon="solar:add-circle-bold-duotone"
                  onClick={() => { setLogForm({ type: 'CALL', subject: '', body: '' }); setShowLogModal(true); }}>
                  Add Log
                </Button>
              }
            />
            {selected.contactLogs.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '12px' }}>
                {selected.contactLogs.slice(0, 5).map((log) => (
                  <div key={log.id} style={{
                    display: 'flex', gap: '12px', alignItems: 'flex-start',
                    background: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)', padding: '12px 14px',
                    border: '1px solid var(--border-base)',
                  }}>
                    <div style={{
                      width: '32px', height: '32px', borderRadius: 'var(--radius-sm)', flexShrink: 0,
                      background: 'rgba(13,148,136,0.10)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Icon icon={contactIcon(log.type)} width={16} style={{ color: 'var(--primary-400)' }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>
                        <span style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '13px' }}>{log.subject}</span>
                        <span style={{ color: 'var(--text-faint)', fontSize: '11px' }}>{formatDate(log.createdAt)}</span>
                      </div>
                      {log.body && <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.5 }}>{log.body}</div>}
                      <div style={{ color: 'var(--text-muted)', fontSize: '11px', marginTop: '4px' }}>
                        {log.type} · {log.agent.name}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ color: 'var(--text-muted)', fontSize: '13px', padding: '20px 0', textAlign: 'center' }}>No contact logs yet.</div>
            )}
          </GlassCard>

          {/* PRIORITY 6: Beneficiaries */}
          <GlassCard>
            <SectionHeader
              title={`Beneficiaries (${selected.beneficiaries.length})`}
              icon="solar:users-group-rounded-bold-duotone"
              actions={
                <Button variant="secondary" size="sm" icon="solar:add-circle-bold-duotone"
                  onClick={() => { setBeneficiaryForm({ firstName: '', lastName: '', relationship: '', phone: '', email: '' }); setShowBeneficiaryModal(true); }}>
                  Add
                </Button>
              }
            />
            {selected.beneficiaries.length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '12px', marginTop: '12px' }}>
                {selected.beneficiaries.map((b) => (
                  <div key={b.id} style={{
                    background: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)',
                    padding: '12px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    border: '1px solid var(--border-base)',
                  }}>
                    <div>
                      <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '14px' }}>{b.firstName} {b.lastName}</div>
                      <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>{b.relationship} {b.phone ? `· ${b.phone}` : ''}</div>
                    </div>
                    <Button variant="icon" onClick={() => handleRemoveBeneficiary(b.id)} style={{ width: '28px', height: '28px' }}>
                      <Icon icon="solar:close-circle-bold-duotone" width={16} />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ color: 'var(--text-muted)', fontSize: '13px', padding: '16px 0' }}>No beneficiaries added yet.</div>
            )}
          </GlassCard>

          {/* PRIORITY 7: Notes */}
          {selected.notes && (
            <GlassCard>
              <SectionHeader title="Notes" icon="solar:document-text-bold-duotone" />
              <p style={{ color: 'var(--text-secondary)', fontSize: '14px', lineHeight: 1.6, marginTop: '8px' }}>{selected.notes}</p>
            </GlassCard>
          )}
        </div>
      </>
    );
  };

  // ─── RENDER: Form View ──────────────────────────────────

  const renderFormView = () => (
    <>
      <TopBar title={editingId ? 'Edit Client' : 'New Client'} />
      <div style={{ padding: '28px', maxWidth: '900px', margin: '0 auto' }}>
        <GlassCard>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
            <Button variant="ghost" icon="solar:arrow-left-bold" onClick={() => setView(selected ? 'detail' : 'list')}>
              Cancel
            </Button>
            <div style={{ flex: 1 }} />
            <Button variant="primary" icon="solar:check-circle-bold-duotone" onClick={handleSaveClient}>
              {editingId ? 'Update' : 'Create'} Client
            </Button>
          </div>

          <SectionHeader title="Basic Information" icon="solar:user-bold-duotone" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '12px' }}>
            <InputField label="First Name" value={formData.firstName} onChange={(e) => setFormData({ ...formData, firstName: e.target.value })} />
            <InputField label="Last Name" value={formData.lastName} onChange={(e) => setFormData({ ...formData, lastName: e.target.value })} />
            <InputField label="Phone" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
            <InputField label="Email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
            <InputField label="Address" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} />
            <InputField label="Birthday" type="date" value={formData.birthday} onChange={(e) => setFormData({ ...formData, birthday: e.target.value })} />
            <InputField label="Occupation" value={formData.occupation} onChange={(e) => setFormData({ ...formData, occupation: e.target.value })} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Income Bracket</label>
              <select value={formData.incomeBracket} onChange={(e) => setFormData({ ...formData, incomeBracket: e.target.value })}
                style={{ background: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: 'var(--radius-md)', padding: '10px 14px', color: 'var(--input-text)', fontFamily: 'var(--font-body)', fontSize: '14px', outline: 'none' }}>
                <option value="">Select...</option>
                {INCOME_BRACKETS.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
          </div>

          <div style={{ margin: '24px 0' }}><Divider /></div>

          <SectionHeader title="CRM Details" icon="solar:info-circle-bold-duotone" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '12px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Source</label>
              <select value={formData.source} onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                style={{ background: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: 'var(--radius-md)', padding: '10px 14px', color: 'var(--input-text)', fontFamily: 'var(--font-body)', fontSize: '14px', outline: 'none' }}>
                {CLIENT_SOURCES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Status</label>
              <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                style={{ background: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: 'var(--radius-md)', padding: '10px 14px', color: 'var(--input-text)', fontFamily: 'var(--font-body)', fontSize: '14px', outline: 'none' }}>
                {CLIENT_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          <div style={{ margin: '24px 0' }}><Divider /></div>

          <SectionHeader title="Emergency Contact" icon="solar:phone-bold-duotone" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '12px' }}>
            <InputField label="Emergency Contact Name" value={formData.emergencyName} onChange={(e) => setFormData({ ...formData, emergencyName: e.target.value })} />
            <InputField label="Emergency Contact Phone" value={formData.emergencyPhone} onChange={(e) => setFormData({ ...formData, emergencyPhone: e.target.value })} />
          </div>

          <div style={{ margin: '24px 0' }}><Divider /></div>

          <SectionHeader title="Notes" icon="solar:document-text-bold-duotone" />
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="Additional notes about this client..."
            style={{
              width: '100%', minHeight: '100px', marginTop: '12px',
              background: 'var(--input-bg)', border: '1px solid var(--input-border)',
              borderRadius: 'var(--radius-md)', padding: '12px 14px', color: 'var(--input-text)',
              fontFamily: 'var(--font-body)', fontSize: '14px', outline: 'none', resize: 'vertical',
            }}
          />
        </GlassCard>
      </div>
    </>
  );

  // ─── Main Render ────────────────────────────────────────

  return (
    <>
      {view === 'list' && renderListView()}
      {view === 'detail' && renderDetailView()}
      {view === 'form' && renderFormView()}

      {/* Beneficiary Modal */}
      {showBeneficiaryModal && (
        <Modal isOpen={showBeneficiaryModal} title="Add Beneficiary" onClose={() => setShowBeneficiaryModal(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '8px 0' }}>
            <InputField label="First Name" value={beneficiaryForm.firstName || ''} onChange={(e) => setBeneficiaryForm({ ...beneficiaryForm, firstName: e.target.value })} />
            <InputField label="Last Name" value={beneficiaryForm.lastName || ''} onChange={(e) => setBeneficiaryForm({ ...beneficiaryForm, lastName: e.target.value })} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Relationship</label>
              <select value={beneficiaryForm.relationship || ''} onChange={(e) => setBeneficiaryForm({ ...beneficiaryForm, relationship: e.target.value })}
                style={{ background: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: 'var(--radius-md)', padding: '10px 14px', color: 'var(--input-text)', fontFamily: 'var(--font-body)', fontSize: '14px', outline: 'none' }}>
                <option value="">Select...</option>
                {RELATIONSHIPS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <InputField label="Phone" value={beneficiaryForm.phone || ''} onChange={(e) => setBeneficiaryForm({ ...beneficiaryForm, phone: e.target.value })} />
            <InputField label="Email" value={beneficiaryForm.email || ''} onChange={(e) => setBeneficiaryForm({ ...beneficiaryForm, email: e.target.value })} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px' }}>
              <Button variant="ghost" onClick={() => setShowBeneficiaryModal(false)}>Cancel</Button>
              <Button variant="primary" icon="solar:check-circle-bold-duotone" onClick={handleAddBeneficiary}>
                Add Beneficiary
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Contact Log Modal */}
      {showLogModal && (
        <Modal isOpen={showLogModal} title="Add Contact Log" onClose={() => setShowLogModal(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '8px 0' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Type</label>
              <select value={logForm.type} onChange={(e) => setLogForm({ ...logForm, type: e.target.value })}
                style={{ background: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: 'var(--radius-md)', padding: '10px 14px', color: 'var(--input-text)', fontFamily: 'var(--font-body)', fontSize: '14px', outline: 'none' }}>
                {CONTACT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <InputField label="Subject" value={logForm.subject} onChange={(e) => setLogForm({ ...logForm, subject: e.target.value })} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Notes</label>
              <textarea
                value={logForm.body}
                onChange={(e) => setLogForm({ ...logForm, body: e.target.value })}
                placeholder="Optional details..."
                style={{
                  width: '100%', minHeight: '80px',
                  background: 'var(--input-bg)', border: '1px solid var(--input-border)',
                  borderRadius: 'var(--radius-md)', padding: '10px 14px', color: 'var(--input-text)',
                  fontFamily: 'var(--font-body)', fontSize: '14px', outline: 'none', resize: 'vertical',
                }}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px' }}>
              <Button variant="ghost" onClick={() => setShowLogModal(false)}>Cancel</Button>
              <Button variant="primary" icon="solar:check-circle-bold-duotone" onClick={handleAddLog}>
                Add Log
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default Clients;
